import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  BookOpenCheck,
  FileText,
  Plus,
  Printer,
  Search,
  Edit3,
  Trash2,
  CheckCircle2,
  Clock,
  X,
  Compass,
  Check,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_META = {
  rascunho: { label: "Rascunho", color: "text-slate-300", bg: "bg-slate-700/40", border: "border-slate-600" },
  revisao: { label: "Em revisão", color: "text-amber-300", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  aprovada: { label: "Aprovada ✓", color: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
};

export const BalaustresView = ({ sessions = [], members = [], currentLoja = null }) => {
  const [balaustres, setBalaustres] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchBalaustres = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== "all" ? `?status_filter=${statusFilter}` : "";
      const res = await axios.get(`${API}/balaustres${params}`);
      setBalaustres(res.data);
      if (res.data.length && !selected) setSelected(res.data[0]);
    } catch (err) {
      toast.error("Erro ao carregar balaústres.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBalaustres();
    // eslint-disable-next-line
  }, [statusFilter]);

  const handleDelete = async (bal) => {
    if (!window.confirm("Excluir este balaústre?")) return;
    try {
      await axios.delete(`${API}/balaustres/${bal.id}`);
      toast.success("Balaústre excluído.");
      setSelected(null);
      fetchBalaustres();
    } catch (err) {
      toast.error("Erro ao excluir.");
    }
  };

  const handleApprove = async (bal) => {
    try {
      await axios.put(`${API}/balaustres/${bal.id}`, { status: "aprovada" });
      toast.success("Balaústre aprovado.");
      fetchBalaustres();
    } catch (err) {
      toast.error("Erro ao aprovar.");
    }
  };

  const author = members.find((m) => m.id === selected?.author_id);

  return (
    <div className="space-y-6" data-testid="balaustres-view">
      {/* Header */}
      <div className="luxury-card p-6 border-l-4 border-l-amber-400">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center space-x-2 text-xs uppercase tracking-wider text-amber-400 font-bold mb-1">
              <BookOpenCheck className="w-4 h-4" />
              <span>Chancelaria • Livro de Balaústres</span>
            </div>
            <h2 className="text-2xl font-serif font-bold text-white">Atas das Sessões (Balaústres)</h2>
            <p className="text-xs text-slate-400 mt-1">
              Redija, revise e aprove as atas litúrgicas das sessões da Loja.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              data-testid="balaustres-select-status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-[#0B111E] border border-slate-700 rounded-lg px-3 py-2 text-xs text-white focus:border-amber-400 outline-none"
            >
              <option value="all">Todos os status</option>
              <option value="rascunho">Rascunhos</option>
              <option value="revisao">Em revisão</option>
              <option value="aprovada">Aprovados</option>
            </select>
            <button
              data-testid="balaustres-btn-new"
              onClick={() => {
                setEditing(null);
                setShowModal(true);
              }}
              className="flex items-center space-x-2 bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold px-4 py-2 rounded-xl text-xs uppercase tracking-wider shadow-[0_0_15px_rgba(251,191,36,0.25)]"
            >
              <Plus className="w-4 h-4" />
              <span>Novo Balaústre</span>
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* List */}
        <div className="luxury-card p-3 lg:col-span-1 max-h-[70vh] overflow-y-auto">
          {loading && <div className="text-slate-500 text-xs p-4">Carregando...</div>}
          {!loading && balaustres.length === 0 && (
            <div className="p-6 text-center text-slate-500 text-xs">
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
              Nenhum balaústre redigido ainda.
            </div>
          )}
          <div className="space-y-1.5">
            {balaustres.map((b) => {
              const meta = STATUS_META[b.status] || STATUS_META.rascunho;
              const isSel = selected?.id === b.id;
              return (
                <button
                  key={b.id}
                  data-testid={`balaustre-item-${b.id}`}
                  onClick={() => setSelected(b)}
                  className={`w-full text-left p-3 rounded-xl border transition-all ${
                    isSel
                      ? "bg-amber-500/10 border-amber-500/60 shadow-md"
                      : "bg-[#080C14] border-slate-800 hover:border-amber-500/30"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <div className="flex items-center space-x-1.5 text-[10px] uppercase tracking-wider text-slate-400">
                        <span className="font-mono text-amber-400">nº {b.session_number || "—"}</span>
                        <span>•</span>
                        <span>{b.session_date || "—"}</span>
                      </div>
                      <div className="text-sm font-semibold text-white mt-0.5 truncate">{b.title}</div>
                    </div>
                    <span className={`shrink-0 ml-2 text-[9px] uppercase font-bold px-2 py-0.5 rounded-full border ${meta.color} ${meta.bg} ${meta.border}`}>
                      {meta.label}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Detail */}
        <div className="lg:col-span-2 space-y-4">
          {!selected ? (
            <div className="luxury-card p-10 text-center">
              <BookOpenCheck className="w-12 h-12 mx-auto text-slate-500 mb-3" />
              <p className="text-slate-400 text-sm">Selecione um balaústre à esquerda ou crie um novo.</p>
            </div>
          ) : (
            <>
              <div className="luxury-card p-6 border-l-4 border-l-amber-400 print:border-l-0">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 print:hidden">
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-amber-400 font-bold flex items-center space-x-2">
                      <Compass className="w-3.5 h-3.5" />
                      <span>Sessão nº {selected.session_number || "—"} • {selected.session_date}</span>
                    </div>
                    <h3 className="text-xl font-serif font-bold text-white mt-1">{selected.title}</h3>
                    {author && (
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        Redigido por {author.name}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      data-testid="balaustre-btn-print"
                      onClick={() => window.print()}
                      className="flex items-center space-x-1.5 bg-[#131C2E] hover:bg-[#1E293B] text-white border border-slate-700 px-3 py-1.5 rounded-lg text-[11px] uppercase tracking-wider font-semibold"
                    >
                      <Printer className="w-3.5 h-3.5" />
                      <span>Imprimir</span>
                    </button>
                    {selected.status !== "aprovada" && (
                      <button
                        data-testid="balaustre-btn-approve"
                        onClick={() => handleApprove(selected)}
                        className="flex items-center space-x-1.5 bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-1.5 rounded-lg text-[11px] uppercase tracking-wider font-bold"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        <span>Aprovar</span>
                      </button>
                    )}
                    <button
                      data-testid="balaustre-btn-edit"
                      onClick={() => {
                        setEditing(selected);
                        setShowModal(true);
                      }}
                      className="flex items-center space-x-1.5 bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold px-3 py-1.5 rounded-lg text-[11px] uppercase tracking-wider"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                      <span>Editar</span>
                    </button>
                    <button
                      data-testid="balaustre-btn-delete"
                      onClick={() => handleDelete(selected)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 border border-slate-700"
                      title="Excluir"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Print header */}
                <div className="hidden print:block text-black mb-6">
                  <div className="flex items-center justify-center flex-col text-center border-b-2 border-black pb-4 mb-4">
                    {currentLoja?.logo_b64 && (
                      <img src={currentLoja.logo_b64} alt="Brasão" className="max-h-20 mb-2 object-contain" />
                    )}
                    <p className="text-xs uppercase tracking-widest">À Glória do Grande Arquiteto do Universo</p>
                    <h1 className="text-xl font-bold mt-1">
                      {currentLoja
                        ? `${currentLoja.name.startsWith("A.R.L.S.") ? "" : "A.R.L.S. "}${currentLoja.name} nº ${currentLoja.number}`
                        : "Sua Loja Maçônica"}
                    </h1>
                    <p className="text-[11px] uppercase tracking-wider">Or∴ {currentLoja?.oriente || "—"} • {currentLoja?.obediencia || ""}</p>
                    <h2 className="text-lg font-bold mt-3">Balaústre — Sessão nº {selected.session_number}</h2>
                    <p className="text-xs mt-1">{selected.session_date}</p>
                  </div>
                </div>

                <div className="prose prose-invert prose-slate max-w-none mt-4 whitespace-pre-wrap text-sm text-slate-200 leading-7 print:text-black">
                  {selected.content || <em className="text-slate-500">Sem conteúdo.</em>}
                </div>

                {/* Print signatures */}
                <div className="hidden print:block mt-16 text-black text-xs">
                  <div className="grid grid-cols-2 gap-8 text-center">
                    <div>
                      {selected.veneravel_signature_b64 && (
                        <img
                          src={selected.veneravel_signature_b64}
                          alt="Assinatura do Venerável"
                          className="max-h-14 mx-auto mb-1 object-contain"
                        />
                      )}
                      <div className="border-t border-black pt-1">Venerável Mestre</div>
                    </div>
                    <div>
                      {selected.chanceler_signature_b64 && (
                        <img
                          src={selected.chanceler_signature_b64}
                          alt="Assinatura do Chanceler"
                          className="max-h-14 mx-auto mb-1 object-contain"
                        />
                      )}
                      <div className="border-t border-black pt-1">Chanceler / Secretário</div>
                    </div>
                  </div>
                </div>

                {/* Preview de assinaturas (só em tela, quando aprovada) */}
                {selected.status === "aprovada" && (selected.veneravel_signature_b64 || selected.chanceler_signature_b64) && (
                  <div className="mt-8 pt-6 border-t border-slate-800 print:hidden">
                    <div className="text-[10px] uppercase text-amber-400 tracking-wider font-bold mb-3">
                      ✧ Assinaturas Digitais
                    </div>
                    <div className="grid grid-cols-2 gap-6 text-center">
                      <div>
                        {selected.veneravel_signature_b64 ? (
                          <img
                            src={selected.veneravel_signature_b64}
                            alt="Venerável"
                            className="max-h-16 mx-auto mb-1 object-contain bg-white/95 rounded p-1"
                            data-testid="balaustre-sig-veneravel-preview"
                          />
                        ) : (
                          <div className="h-16 text-[10px] text-slate-500 italic flex items-end justify-center">— sem assinatura —</div>
                        )}
                        <div className="text-[11px] text-slate-300 border-t border-slate-700 pt-1">Venerável Mestre</div>
                      </div>
                      <div>
                        {selected.chanceler_signature_b64 ? (
                          <img
                            src={selected.chanceler_signature_b64}
                            alt="Chanceler"
                            className="max-h-16 mx-auto mb-1 object-contain bg-white/95 rounded p-1"
                            data-testid="balaustre-sig-chanceler-preview"
                          />
                        ) : (
                          <div className="h-16 text-[10px] text-slate-500 italic flex items-end justify-center">— sem assinatura —</div>
                        )}
                        <div className="text-[11px] text-slate-300 border-t border-slate-700 pt-1">Chanceler / Secretário</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {showModal && (
        <BalaustreModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditing(null);
          }}
          onSaved={(saved) => {
            setShowModal(false);
            setEditing(null);
            fetchBalaustres();
            if (saved) setSelected(saved);
          }}
          initialData={editing}
          sessions={sessions}
          members={members}
        />
      )}
    </div>
  );
};

const BalaustreModal = ({ isOpen, onClose, onSaved, initialData, sessions, members }) => {
  const [form, setForm] = useState({
    session_id: "",
    title: "",
    content: "",
    author_id: "",
    status: "rascunho",
    veneravel_signature_b64: "",
    chanceler_signature_b64: "",
  });
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  useEffect(() => {
    if (isOpen && !initialData) {
      axios.get(`${API}/balaustre-templates`)
        .then((r) => setTemplates(r.data))
        .catch(() => {});
    }
  }, [isOpen, initialData]);

  useEffect(() => {
    if (initialData) {
      setForm({
        session_id: initialData.session_id,
        title: initialData.title,
        content: initialData.content,
        author_id: initialData.author_id || "",
        status: initialData.status,
        veneravel_signature_b64: initialData.veneravel_signature_b64 || "",
        chanceler_signature_b64: initialData.chanceler_signature_b64 || "",
      });
    } else {
      setForm({ session_id: "", title: "", content: "", author_id: "", status: "rascunho", veneravel_signature_b64: "", chanceler_signature_b64: "" });
    }
  }, [initialData]);

  const handleSignatureUpload = (which) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 300 * 1024) {
      toast.error("Assinatura muito grande (máx 300 KB). Use PNG comprimido.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setForm((f) => ({ ...f, [which]: ev.target.result }));
      toast.success("Assinatura carregada.");
    };
    reader.readAsDataURL(file);
  };

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let res;
      if (initialData?.id) {
        res = await axios.put(`${API}/balaustres/${initialData.id}`, form);
        toast.success("Balaústre atualizado.");
      } else {
        res = await axios.post(`${API}/balaustres`, form);
        toast.success("Balaústre criado.");
      }
      onSaved(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar balaústre.");
    }
  };

  const suggestTemplate = () => {
    const sess = sessions.find((s) => s.id === form.session_id);
    if (!sess) {
      toast.error("Selecione a sessão primeiro.");
      return;
    }
    const lojaName = "sua Loja";
    const template = `Aos ${sess.date}, reuniu-se a Augusta e Respeitável Loja Simbólica "${lojaName}" em ${sess.session_type}.

ABERTURA DOS TRABALHOS
Aberta a sessão no Grau ${sess.degree_required} pelo Venerável Mestre.

ORDEM DO DIA
${sess.agenda || "—"}

TRONCO DE BENEFICÊNCIA
Foi levantado o Tronco da Viúva, arrecadando R$ ${(sess.trunk_amount || 0).toFixed(2)}.

VISITANTES
${sess.visitors || "Nenhum visitante presente."}

ENCERRAMENTO
Nada mais havendo a tratar, o Venerável Mestre encerrou os trabalhos na forma do estilo.

E, para constar, eu, Chanceler / Secretário, lavrei o presente balaústre.

À Glória do Grande Arquiteto do Universo.`;
    setForm({ ...form, content: template, title: form.title || `Balaústre da Sessão nº ${sess.session_number}` });
    toast.success("Template ritualístico inserido.");
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="balaustre-modal"
    >
      <div
        className="w-full max-w-3xl max-h-[92vh] bg-[#0B111E] border border-amber-500/40 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-slate-800 bg-[#080D18] flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-amber-400 font-bold flex items-center space-x-2">
              <BookOpenCheck className="w-3.5 h-3.5" />
              <span>Chancelaria • {initialData ? "Editar" : "Novo"} Balaústre</span>
            </div>
            <h2 className="text-lg font-serif font-bold text-white mt-1">
              {initialData?.title || "Nova ata litúrgica"}
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
          {!initialData && templates.length > 0 && (
            <div className="bg-amber-500/5 border border-amber-500/30 rounded-lg p-3">
              <label className="text-[10px] uppercase text-amber-400 tracking-wider font-bold flex items-center space-x-1.5">
                <span>✨</span>
                <span>Iniciar do modelo ritualístico</span>
              </label>
              <div className="flex space-x-2 mt-2">
                <select
                  data-testid="balaustre-input-template"
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                  className="flex-1 bg-[#0B111E] border border-slate-700 rounded-lg px-3 py-2 text-xs text-white focus:border-amber-400 outline-none"
                >
                  <option value="">— Escolher template —</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.session_type} — {t.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  data-testid="balaustre-btn-load-template"
                  onClick={() => {
                    const tpl = templates.find((t) => t.id === selectedTemplateId);
                    if (!tpl) {
                      toast.error("Selecione um template.");
                      return;
                    }
                    setForm({ ...form, content: tpl.content, title: form.title || tpl.name });
                    toast.success(`Template "${tpl.name}" carregado.`);
                  }}
                  className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold px-3 py-2 rounded-lg text-[10px] uppercase tracking-wider"
                >
                  Aplicar
                </button>
              </div>
            </div>
          )}

          {!initialData && (
            <div>
              <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">Sessão *</label>
              <select
                data-testid="balaustre-input-session"
                required
                value={form.session_id}
                onChange={(e) => setForm({ ...form, session_id: e.target.value })}
                className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-400 outline-none"
              >
                <option value="">— Selecionar sessão —</option>
                {sessions.map((s) => (
                  <option key={s.id} value={s.id}>
                    nº {s.session_number} — {s.date} — {s.title}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">Título *</label>
            <input
              data-testid="balaustre-input-title"
              type="text"
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-400 outline-none"
              placeholder="Ex: Balaústre da Sessão Econômica nº 1.423"
            />
          </div>

          <div className="flex items-center justify-between">
            <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">Conteúdo da ata *</label>
            <button
              type="button"
              data-testid="balaustre-btn-template"
              onClick={suggestTemplate}
              className="text-[10px] uppercase font-bold text-amber-400 hover:text-amber-300 tracking-wider"
            >
              ✨ Inserir template ritualístico
            </button>
          </div>
          <textarea
            data-testid="balaustre-input-content"
            required
            rows={16}
            value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })}
            className="w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-400 outline-none font-mono leading-6 resize-none"
            placeholder="Descreva os trabalhos litúrgicos da sessão..."
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">Autor (Chanceler/Secretário)</label>
              <select
                data-testid="balaustre-input-author"
                value={form.author_id || ""}
                onChange={(e) => setForm({ ...form, author_id: e.target.value })}
                className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-400 outline-none"
              >
                <option value="">— Selecionar —</option>
                {members.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">Status</label>
              <select
                data-testid="balaustre-input-status"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-400 outline-none"
              >
                <option value="rascunho">Rascunho</option>
                <option value="revisao">Em revisão</option>
                <option value="aprovada">Aprovada</option>
              </select>
            </div>
          </div>

          {/* Assinaturas Digitais (PNG) */}
          <div className="pt-4 border-t border-slate-800">
            <div className="flex items-center space-x-2 text-[10px] uppercase text-amber-400 tracking-wider font-bold mb-2">
              <span>✧</span>
              <span>Assinaturas Digitais (PNG transparente, máx 300 KB)</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SignatureField
                label="Assinatura do Venerável Mestre"
                value={form.veneravel_signature_b64}
                onFile={handleSignatureUpload("veneravel_signature_b64")}
                onClear={() => setForm({ ...form, veneravel_signature_b64: "" })}
                testid="balaustre-input-sig-veneravel"
              />
              <SignatureField
                label="Assinatura do Chanceler / Secretário"
                value={form.chanceler_signature_b64}
                onFile={handleSignatureUpload("chanceler_signature_b64")}
                onClear={() => setForm({ ...form, chanceler_signature_b64: "" })}
                testid="balaustre-input-sig-chanceler"
              />
            </div>
            <p className="text-[10px] text-slate-500 mt-2 italic">
              As assinaturas serão automaticamente incluídas no balaústre impresso após aprovação.
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-slate-800">
            <button
              type="button"
              onClick={onClose}
              className="text-xs uppercase tracking-wider text-slate-400 hover:text-white font-semibold px-4"
            >
              Cancelar
            </button>
            <button
              type="submit"
              data-testid="balaustre-btn-submit"
              className="flex items-center space-x-2 bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold px-5 py-2.5 rounded-xl text-xs uppercase tracking-wider"
            >
              <Check className="w-3.5 h-3.5" />
              <span>{initialData ? "Atualizar" : "Salvar Balaústre"}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const SignatureField = ({ label, value, onFile, onClear, testid }) => (
  <div className="rounded-xl border border-slate-700 bg-[#131C2E] p-3">
    <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold block mb-2">
      {label}
    </label>
    {value ? (
      <div className="space-y-2">
        <div className="bg-white rounded-lg p-2 flex items-center justify-center min-h-[70px]">
          <img src={value} alt={label} className="max-h-16 object-contain" data-testid={`${testid}-preview`} />
        </div>
        <button
          type="button"
          onClick={onClear}
          className="w-full text-[10px] uppercase text-rose-300 hover:text-rose-200 font-bold tracking-wider py-1"
        >
          Remover assinatura
        </button>
      </div>
    ) : (
      <label className="flex items-center justify-center h-[80px] border-2 border-dashed border-slate-600 rounded-lg text-slate-400 hover:border-amber-400 hover:text-amber-300 cursor-pointer text-xs font-semibold uppercase tracking-wider transition-colors">
        Clique para enviar PNG
        <input
          data-testid={testid}
          type="file"
          accept="image/png,image/jpeg"
          onChange={onFile}
          className="hidden"
        />
      </label>
    )}
  </div>
);
